import { useProfileStore } from '../store/useProfileStore';
import { getById as apiGetById, put as apiPut, patch as apiPatch, getSilent } from './apiService';
import { search } from './searchService';
import { logTrace } from './loggerService';
import { Profile } from '../types/profile';
import type { ProfileByEmailResponse } from '../types/profile';
import type { SearchParams } from '../types/search';
import type { UserAuditReportRow } from '../types/userAudit';
import { searchAllBusinesses, TEAM_SEARCH_EXPAND } from './businessService';
import { profileToUserAuditRow, sortUserAuditRows } from '../utils/userAuditUtils';
import { getSummaryMember } from '../types/teamSummary';

// Setter for active profile
export function setActiveProfile(profile: Profile | null) {
  useProfileStore.getState().setActiveProfile(profile);
}

// Getter for active profile
export function getActiveProfile(): Profile | null {
  const profile = useProfileStore.getState().activeProfile;
  logTrace('getActiveProfile', undefined, { activeProfile: profile });
  return profile;
}

// API calls
export async function getById(profileId: string, serviceKey?: string): Promise<Profile> {
  return apiGetById<Profile>('/profile', profileId, 'expand=userPlan', serviceKey);
}

export async function put(profile: Profile): Promise<Profile> {
  return apiPut<Profile>(`/profile/${profile.id}`, profile);
}

export async function patch(profile: Partial<Profile> & { id: string }): Promise<Profile> {
  return apiPatch<Profile>(`/profile/${profile.id}`, profile);
}

// Search profiles
export interface ProfileFilter {
  persona: string,
  businessId?: string;
}

export async function searchProfiles(
  filter: ProfileFilter = {persona: 'business'},
  params: SearchParams = {},
): Promise<Profile[]> {
  return search<Profile>('/profile/search', filter, params);
}

// Set user trial
export async function setUserTrial(
  profileId: string,
  data: { certificationId: string; expires: string },
): Promise<unknown> {
  return apiPut(`/profile/${profileId}/trial`, data);
}

export async function setSlackActivityNotify(
  profileId: string,
  enabled: boolean,
): Promise<unknown> {
  return apiPut(`/profile/${profileId}/slack-activity-notify`, { enabled });
}

export async function getUserAuditReport(): Promise<UserAuditReportRow[]> {
  const teams = await searchAllBusinesses(
    {},
    { pageSize: 1000, orderBy: 'updated', order: 'desc', expand: TEAM_SEARCH_EXPAND },
  );
  const teamsById = new Map(teams.map((team) => [team.id, team]));

  const pageSize = 1000;
  const searchParams: SearchParams = {
    pageSize,
    orderBy: 'updated',
    order: 'desc',
    expand: 'business.client,userPlan,lastAuthentication',
  };

  const profiles: Profile[] = [];
  let page = 1;

  while (true) {
    const batch = await searchProfiles({ persona: 'business' }, { ...searchParams, page });
    profiles.push(...batch);
    if (batch.length < pageSize) break;
    page += 1;
  }

  return sortUserAuditRows(
    profiles.map((profile) => profileToUserAuditRow(
      profile,
      getSummaryMember(teamsById.get(profile.businessId)?.summary, profile.id),
    )),
  );
}

// Lookup profile by email (returns null if not found)
export async function getProfileByEmail(email: string): Promise<ProfileByEmailResponse | null> {
  return getSilent<ProfileByEmailResponse>(`/profile/email?email=${encodeURIComponent(email)}&persona=business&expand=teamPlan`);
}

// Convenience export
export const profileService = {
  setActiveProfile,
  getActiveProfile,
  getById,
  put,
  patch,
  searchProfiles,
  setUserTrial,
  setSlackActivityNotify,
};
